/**
 * SkillManagerPage — 技能管理中心
 *
 * Full skill lifecycle management:
 * - View all registered skills (native + dynamic)
 * - Enable/disable per tenant
 * - View skill details (intents, permissions, retry, escalation)
 * - Skill execution statistics
 * - Create dynamic skills (prompt-driven / tool-composed)
 *
 * Data from: GET /api/v1/skills, POST /enable, POST /disable, PATCH /config
 * Stats from: GET /api/v1/logs/skill-stats
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Zap,
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Shield,
  Cpu,
  Clock,
  CheckCircle,
  Loader2,
  Play,
  Pause,
  Bot,
  Code,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ─── Types ─────────────────────────────────────────

interface Skill {
  id: string;
  name: string;
  version: string;
  enabled: boolean;
  intents: string[];
  permissions: string[];
  retryPolicy: { max: number; delayMs?: number };
  escalation: 'human' | 'auto';
  riskClass?: string;
  executionLayer?: string;
  description?: string;
  tenantConfig?: Record<string, unknown>;
  connectorBindingCount?: number;
  policyCount?: number;
}

interface SkillStats {
  skill_id: string;
  total_executions: number;
  success_count: number;
  failure_count: number;
  timeout_count: number;
  avg_duration_ms: number;
}

interface TenantOption {
  id: string;
  name: string;
}

// ─── Component ─────────────────────────────────────

export default function SkillManagerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, switchTenant } = useAuth();
  const [loading, setLoading] = useState(true);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [stats, setStats] = useState<Map<string, SkillStats>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [toggling, setToggling] = useState<Set<string>>(new Set());
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(user?.tenantId ?? '');

  const isPlatformAdmin = user?.role === 'platform_admin';

  const fetchData = useCallback(async () => {
    setLoading(true);
    const skillsPath = isPlatformAdmin && selectedTenantId
      ? `/platform/tenants/${selectedTenantId}/skills`
      : '/skills?limit=100';
    const [skillsRes, statsRes] = await Promise.allSettled([
      api.get<{ items: Skill[]; total: number }>(skillsPath),
      isPlatformAdmin && selectedTenantId && selectedTenantId !== user?.tenantId
        ? Promise.resolve({ items: [] as SkillStats[] })
        : api.get<{ items: SkillStats[] }>('/logs/skill-stats?limit=100'),
    ]);

    if (skillsRes.status === 'fulfilled') {
      setSkills(skillsRes.value.items ?? []);
    }
    if (statsRes.status === 'fulfilled') {
      const map = new Map<string, SkillStats>();
      for (const s of statsRes.value.items ?? []) {
        map.set(s.skill_id, s);
      }
      setStats(map);
    }
    setLoading(false);
  }, [isPlatformAdmin, selectedTenantId, user?.tenantId]);

  useEffect(() => {
    setSelectedTenantId((prev) => prev || user?.tenantId || '');
  }, [user?.tenantId]);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    api.get<Array<{ id: string; name: string }>>('/platform/tenants')
      .then((rows) => setTenantOptions(rows.map((row) => ({ id: row.id, name: row.name }))))
      .catch(console.error);
  }, [isPlatformAdmin]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  const handleToggle = async (skillId: string, currentEnabled: boolean) => {
    setToggling((prev) => new Set(prev).add(skillId));
    try {
      const togglePath = isPlatformAdmin && selectedTenantId
        ? `/platform/tenants/${selectedTenantId}/skills/${skillId}`
        : `/skills/${skillId}`;
      if (currentEnabled) {
        await api.post(`${togglePath}/disable`, {});
      } else {
        await api.post(`${togglePath}/enable`, {});
      }
      setSkills((prev) =>
        prev.map((s) => (s.id === skillId ? { ...s, enabled: !s.enabled } : s)),
      );
    } catch (err) {
      console.error('Toggle failed:', err);
    } finally {
      setToggling((prev) => {
        const next = new Set(prev);
        next.delete(skillId);
        return next;
      });
    }
  };

  const openTenantPolicies = useCallback(async () => {
    // Direct tenant proxy access has been removed for platform admin
    toast.error('Platform admin cannot proxy to tenant session');
  }, []);

  const filtered = searchQuery
    ? skills.filter(
        (s) =>
          s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.intents.some((i) => i.toLowerCase().includes(searchQuery.toLowerCase())),
      )
    : skills;

  const enabledCount = skills.filter((s) => s.enabled).length;
  const nativeCount = skills.filter((s) => !s.executionLayer || s.executionLayer === 'native').length;
  const dynamicCount = skills.filter((s) => s.executionLayer && s.executionLayer !== 'native').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* ─── Header ───────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6 text-yellow-500" />
            Skill Manager
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage all AI employee skills — enable, disable, configure, monitor execution
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </Button>
      </div>

      {isPlatformAdmin && (
        <div className="flex max-w-3xl items-end gap-3">
          <div className="max-w-sm flex-1">
            <p className="mb-2 text-xs text-muted-foreground">Tenant filter</p>
            <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
              <SelectTrigger>
                <SelectValue placeholder="Select tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenantOptions.map((tenant) => (
                  <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => void openTenantPolicies()} disabled={!selectedTenantId}>
            Open Tenant Policies
          </Button>
        </div>
      )}

      {/* ─── Summary Cards ────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniCard label="Total Skills" value={skills.length} icon={<Zap className="h-4 w-4 text-yellow-500" />} />
        <MiniCard label="Enabled" value={enabledCount} icon={<CheckCircle className="h-4 w-4 text-green-500" />} />
        <MiniCard label="Native Skills" value={nativeCount} icon={<Code className="h-4 w-4 text-primary" />} />
        <MiniCard label="Dynamic Skills" value={dynamicCount} icon={<Bot className="h-4 w-4 text-purple-500" />} />
      </div>

      {/* ─── Search ───────────────────────────── */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search skill name, ID or intent…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* ─── Skill List ───────────────────────── */}
      <div className="space-y-2">
        {filtered.map((skill) => {
          const isExpanded = expandedId === skill.id;
          const stat = stats.get(skill.id);
          const isToggling = toggling.has(skill.id);
          const successRate =
            stat && stat.total_executions > 0
              ? ((stat.success_count / stat.total_executions) * 100).toFixed(1)
              : null;

          return (
            <Card key={skill.id} className={cn(!skill.enabled && 'opacity-60')}>
              <CardContent className="py-3 px-4">
                {/* Main Row */}
                <div className="flex items-center gap-3">
                  {/* Enable/Disable Toggle */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => void handleToggle(skill.id, skill.enabled)}
                    disabled={isToggling}
                    title={skill.enabled ? 'Click to disable' : 'Click to enable'}
                  >
                    {isToggling ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : skill.enabled ? (
                      <Pause className="h-4 w-4 text-green-500" />
                    ) : (
                      <Play className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>

                  {/* Skill Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{skill.name}</span>
                      <Badge variant="outline" className="text-[10px]">v{skill.version}</Badge>
                      {skill.executionLayer && skill.executionLayer !== 'native' && (
                        <Badge variant="secondary" className="text-[10px]">
                          {skill.executionLayer === 'prompt_driven' ? '🧠 Prompt' :
                           skill.executionLayer === 'tool_composed' ? '🔧 Tool' :
                           skill.executionLayer === 'sandbox_bundle' ? '📦 Sandbox' :
                           skill.executionLayer}
                        </Badge>
                      )}
                      {skill.riskClass && (
                        <Badge variant={skill.riskClass.startsWith('R0') || skill.riskClass.startsWith('R1') ? 'default' : 'destructive'} className="text-[10px]">
                          {skill.riskClass}
                        </Badge>
                      )}
                      {(skill.connectorBindingCount ?? 0) > 0 && (
                        <Badge variant="outline" className="text-[10px]">
                          {skill.connectorBindingCount} bindings
                        </Badge>
                      )}
                      {(skill.policyCount ?? 0) > 0 && (
                        <Badge variant="outline" className="text-[10px]">
                          {skill.policyCount} policies
                        </Badge>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {skill.intents.slice(0, 3).join(', ')}
                      {skill.intents.length > 3 && ` +${skill.intents.length - 3}`}
                    </p>
                  </div>

                  {/* Stats Quick View */}
                  {stat && (
                    <div className="hidden md:flex items-center gap-4 text-xs text-muted-foreground">
                      <span title="Executions">
                        <Cpu className="h-3 w-3 inline mr-1" />
                        {stat.total_executions}
                      </span>
                      <span title="Success Rate" className={cn(
                        successRate && parseFloat(successRate) > 90 ? 'text-green-600' :
                        successRate && parseFloat(successRate) > 70 ? 'text-yellow-600' : 'text-red-600',
                      )}>
                        {successRate ?? '0'}%
                      </span>
                      <span title="Avg Duration">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {stat.avg_duration_ms.toFixed(0)}ms
                      </span>
                    </div>
                  )}

                  {/* Escalation badge */}
                  <Badge variant={skill.escalation === 'human' ? 'default' : 'secondary'} className="text-[10px] shrink-0">
                    {skill.escalation === 'human' ? '🧑 Human Escalation' : '🤖 Auto'}
                  </Badge>

                  {/* Expand */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => setExpandedId(isExpanded ? null : skill.id)}
                  >
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h4 className="font-medium text-xs text-muted-foreground mb-1">Intents</h4>
                      <div className="flex flex-wrap gap-1">
                        {skill.intents.map((intent) => (
                          <Badge key={intent} variant="outline" className="text-[10px]">{intent}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-xs text-muted-foreground mb-1">Permissions</h4>
                      <div className="flex flex-wrap gap-1">
                        {skill.permissions.map((perm) => (
                          <Badge key={perm} variant="outline" className="text-[10px]">
                            <Shield className="h-2.5 w-2.5 mr-0.5" />{perm}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-xs text-muted-foreground mb-1">Retry Policy</h4>
                      <p className="text-xs">
                        Max {skill.retryPolicy.max} retries
                        {skill.retryPolicy.delayMs ? `, delay ${skill.retryPolicy.delayMs}ms` : ''}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-medium text-xs text-muted-foreground mb-1">Connector Layer</h4>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <span>Bindings: <strong>{skill.connectorBindingCount ?? 0}</strong></span>
                        <span>Policies: <strong>{skill.policyCount ?? 0}</strong></span>
                      </div>
                    </div>
                    {stat && (
                      <div>
                        <h4 className="font-medium text-xs text-muted-foreground mb-1">Execution Stats</h4>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <span>Total: <strong>{stat.total_executions}</strong></span>
                          <span>Success: <strong className="text-green-600">{stat.success_count}</strong></span>
                          <span>Failed: <strong className="text-red-600">{stat.failure_count}</strong></span>
                          <span>Timeout: <strong className="text-yellow-600">{stat.timeout_count}</strong></span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            {searchQuery ? `No skills matching "${searchQuery}"` : 'No registered skills'}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────

function MiniCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <p className="text-[10px] text-muted-foreground">{label}</p>
            <p className="text-xl font-bold">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
